import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { useGetTradeOffersByUserQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';
import { formatBucketTick, formatBucketLabel } from '../utils/formatBucket';
import { colorFromString, OTHER_COLOR, OTHER_LABEL } from 'components/admin/charts/palettes';

/**
 * Trade Offers Sent by User — power-sender view, segmented by proposing username.
 *
 * Deliberately a near-clone of SmsAlertsChart rather than a use of the shared <StackedBarChart>:
 * this chart needs the "username (count)" multi-line legend and the per-user breakdown tooltip,
 * and the shared component's <Tooltip> is not overridable through props. That is the same reason
 * SmsAlertsChart hand-rolls its recharts markup. The two response shapes are field-for-field
 * identical, so if a third by-user chart ever appears, extract one component and migrate all of
 * them at once — a half-extraction would leave three implementations of the same chart.
 */

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

// Same mapping as SmsAlertsChart, so a username keeps one color across both charts.
const colorForUser = (username: string): string =>
  username === OTHER_LABEL ? OTHER_COLOR : colorFromString(username);

interface TooltipEntry {
  name?: string;
  value?: number;
  color?: string;
}

const UserBreakdownTooltip: React.FC<{
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
  period: AnalyticsPeriod;
}> = ({ active, payload, label, period }) => {
  if (!active || !payload || !payload.length || !label) return null;
  const entries = payload
    .filter((p) => (p.value ?? 0) > 0)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  if (!entries.length) return null;
  const total = entries.reduce((sum, e) => sum + (e.value ?? 0), 0);
  const top = entries.slice(0, 10);
  const moreCount = entries.length - top.length;
  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '8px',
        color: '#fff',
        padding: '8px 12px',
        fontSize: 13,
        maxWidth: 260
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{formatBucketLabel(label, period)}</div>
      <div style={{ color: '#999', marginBottom: 6 }}>{total.toLocaleString()} trade offers</div>
      {top.map((e) => (
        <div key={e.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ color: e.color }}>{e.name}</span>
          <span>{(e.value ?? 0).toLocaleString()}</span>
        </div>
      ))}
      {moreCount > 0 && (
        <div style={{ color: '#999', marginTop: 4 }}>+{moreCount} more users</div>
      )}
    </div>
  );
};

const TradeOffersByUserChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error, refetch } = useGetTradeOffersByUserQuery({ period });

  // Segments = every proposer appearing in the period, ordered by total volume descending so the
  // heaviest sender stays at the bottom of every bar; "Other" always stacks last (on top).
  const segments = useMemo<string[]>(() => {
    if (!data?.data) return [];
    const totals: Record<string, number> = {};
    data.data.forEach((day) => {
      day.perUser.forEach(({ username, count }) => {
        totals[username] = (totals[username] || 0) + count;
      });
    });
    return Object.keys(totals).sort((a, b) => {
      if (a === OTHER_LABEL) return 1;
      if (b === OTHER_LABEL) return -1;
      return totals[b] - totals[a];
    });
  }, [data]);

  const chartData = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((day) => {
      const row: Record<string, number | string> = { date: day.date };
      day.perUser.forEach(({ username, count }) => {
        row[username] = count;
      });
      return row;
    });
  }, [data]);

  const grandTotal = useMemo(
    () => (data?.data || []).reduce((sum, d) => sum + d.total, 0),
    [data]
  );

  const periodNoun = period === 7 ? 'week' : period === 30 ? 'month' : 'year';

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Trade Offers Sent by User</h2>
          <p className="chart-subtitle">Daily trade offers created, by proposer (UTC)</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend-multi">
            {(data?.topUsers || []).slice(0, 10).map((u) => (
              <div key={u.username} className="chart-legend">
                <span className="legend-dot" style={{ backgroundColor: colorForUser(u.username) }} />
                <span className="legend-label">
                  {u.username} ({u.count.toLocaleString()})
                </span>
              </div>
            ))}
            {!!data?.otherUserCount && (
              <div className="chart-legend">
                <span className="legend-dot" style={{ backgroundColor: OTHER_COLOR }} />
                <span className="legend-label">Other ({data.otherUserCount} users)</span>
              </div>
            )}
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">
            Failed to load data
            <button className="chart-retry-btn" onClick={() => refetch()}>Retry</button>
          </div>
        ) : data ? (
          <div className="chart-empty-wrap">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatBucketTick}
                  stroke="#666"
                  tick={{ fill: '#999', fontSize: 12 }}
                  axisLine={{ stroke: '#2a2a2a' }}
                  tickLine={false}
                  interval={chartData.length > 60 ? Math.floor(chartData.length / 12) : 0}
                />
                <YAxis
                  stroke="#666"
                  tick={{ fill: '#999', fontSize: 12 }}
                  axisLine={{ stroke: '#2a2a2a' }}
                  tickLine={false}
                  allowDecimals={false}
                  label={{
                    value: 'Trade Offers Sent',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#999', fontSize: 12, textAnchor: 'middle' }
                  }}
                />
                <Tooltip
                  content={<UserBreakdownTooltip period={period} />}
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                />
                {segments.map((seg, index) => (
                  <Bar
                    key={seg}
                    dataKey={seg}
                    stackId="tradeOffers"
                    fill={colorForUser(seg)}
                    radius={index === segments.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    name={seg}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            {grandTotal === 0 && (
              <div className="chart-empty">No trade offers sent in this period</div>
            )}
          </div>
        ) : null}
      </div>

      {data && (
        <div className="chart-summary">
          Total: {grandTotal.toLocaleString()} trade offers sent across {data.uniqueUserCount.toLocaleString()} unique users in the last {periodNoun}
        </div>
      )}
    </div>
  );
};

export default TradeOffersByUserChart;
